"use client";

/**
 * Slim, chrome-less clip player for iframe embeds. No header, sidebar, comments
 * or reactions — just the video, filling a 16:9 frame on the host page. Only
 * public clips play; an internal clip stays restricted for anonymous visitors.
 */

import { ClipSharePlayer } from "@/app/(dashboard)/_components/clips/share/clip-share-player";
import { usePublicOrgHostGate } from "@/lib/public-org-host-gate";
import { api } from "@v1/backend/convex/_generated/api";
import type { PublicClipView } from "@v1/backend/logic/clips/access";
import { useQuery } from "convex/react";
import { Lock } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useRef } from "react";

export function ClipEmbedView({
  clipId,
  initialClip,
  autoPlay = false,
}: {
  readonly clipId: string;
  readonly initialClip: PublicClipView;
  readonly autoPlay?: boolean;
}): ReactElement {
  const t = useTranslations("public.clipEmbedView");
  const liveClip =
    useQuery(api.clips.public.getPublicClipById, { clip_id: clipId }) ??
    initialClip;
  // Server-rendered: redirect only, no blank page while checking.
  usePublicOrgHostGate(liveClip.org_slug);
  const videoRef = useRef<HTMLVideoElement>(null);
  const granted = liveClip.access === "granted";

  return (
    <div className="flex h-screen w-full items-center justify-center overflow-hidden bg-black">
      {granted ? (
        <ClipSharePlayer
          clipId={liveClip.id}
          posterUrl={liveClip.poster_url}
          videoRef={videoRef}
          autoPlay={autoPlay}
          seekingDisabled={liveClip.seeking_disabled}
        />
      ) : (
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 text-white/70">
          <Lock className="h-6 w-6" />
          <p className="text-sm">{t("dieserClipPrivat")}</p>
        </div>
      )}
    </div>
  );
}
